<template>
  <div class="plugin-version">
    <div class="panel-page">
      <div class="panel-heading">
        <h2>版本管理</h2>
        <span class="pull-right">
          <el-button size="small" icon="el-icon-back" @click="goBack">返回列表</el-button>
          <el-button type="primary" size="small" icon="el-icon-upload" @click="openAdd">上传新版本</el-button>
        </span>
      </div>

      <div class="plugin-summary">
        <p class="summary-title">{{ plugin.pluginName || '-' }}</p>
        <p class="summary-sub">
          代码名：{{ plugin.codeName || '-' }} | 基础版：{{ formatYuan(plugin.pluginPrice) }} 元 | 高级版：{{ formatYuan(plugin.pluginPrice2) }} 元
        </p>
      </div>

      <div class="alert alert-info-light">
        每个版本上传一个 zip 代码包（最大 500M）。版本介绍与更新日志支持 Markdown 语法，将展示在插件详情页。
        插件一旦产生成交订单，版本将不可删除（买家需要继续下载历史版本），仅可编辑内容。
      </div>

      <el-table v-loading="loading" :data="versions" style="width: 100%">
        <el-table-column label="版本号" prop="infoVersion" width="120" />
        <el-table-column label="版本介绍" min-width="200">
          <template slot-scope="scope">
            <span>{{ mdToPlain(scope.row.infoContent) || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="更新日志" min-width="220">
          <template slot-scope="scope">
            <span>{{ mdToPlain(scope.row.infoUpdate) || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="代码包" width="165">
          <template slot-scope="scope">
            <template v-if="scope.row.infoCode">
              <el-tag type="success" size="small">已上传</el-tag>
              <el-button size="mini" type="text" icon="el-icon-download" @click="downloadCode(scope.row)">下载</el-button>
            </template>
            <el-tag v-else type="danger" size="small">缺失</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="上传时间" width="160">
          <template slot-scope="scope">{{ formatTime(scope.row.createdAt) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="130" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="text" icon="el-icon-edit" @click="openEdit(scope.row)">编辑</el-button>
            <el-button size="mini" type="text" icon="el-icon-delete" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div v-if="!loading && versions.length === 0" class="empty-box">
        <i class="el-icon-upload2 empty-icon"></i>
        <p>还没有上传任何版本，买家暂时无法下载</p>
        <el-button type="primary" @click="openAdd">上传第一个版本</el-button>
      </div>

      <!-- 新增 / 编辑版本 -->
      <el-dialog
        :title="isEdit ? '编辑版本' : '上传新版本'"
        :visible.sync="dialogOpen"
        width="90%"
        :close-on-click-modal="false"
        :destroy-on-close="true"
        append-to-body>
        <el-form ref="versionForm" :model="form" :rules="rules" label-width="100px">
          <el-form-item label="版本号" prop="infoVersion">
            <el-input v-model="form.infoVersion" placeholder="如 1.0.0 / v2.1" style="width: 260px" />
          </el-form-item>

          <el-form-item label="版本介绍">
            <mavon-editor
              ref="mdContent"
              v-model="form.infoContent"
              @imgAdd="handleContentImgAdd" />
            <p class="field-tip">支持 Markdown 语法，展示在插件详情页「插件介绍」标签</p>
          </el-form-item>

          <el-form-item label="更新日志">
            <mavon-editor
              ref="mdUpdate"
              v-model="form.infoUpdate"
              @imgAdd="handleUpdateImgAdd" />
            <p class="field-tip">支持 Markdown 语法，展示在插件详情页「版本更新」标签</p>
          </el-form-item>

          <el-form-item label="代码包" prop="fileUrl">
            <div v-if="isEdit && editVersionHadCode" class="current-code">
              <span class="current-code-label">当前代码包：</span>
              <span class="current-code-name">{{ editVersionCodeName || '已上传' }}</span>
              <el-button
                size="mini"
                type="text"
                icon="el-icon-download"
                @click="downloadCode({ infoId: form.infoId, infoVersion: form.infoVersion })">下载</el-button>
            </div>
            <el-upload
              drag
              :headers="headers"
              :action="uploadAction"
              :show-file-list="false"
              :on-success="handleCodeSuccess"
              :on-error="handleCodeError"
              :before-upload="beforeCodeUpload">
              <div v-if="!form.fileUrl" class="upload-drag">
                <i class="el-icon-upload"></i>
                <div class="el-upload__text">将 zip 代码包拖到此处，或<em>点击上传</em></div>
                <div class="el-upload__tip">
                  {{ isEdit && editVersionHadCode ? '当前版本已有代码包，不重新上传则保持不变' : '仅支持 zip 压缩包，最大 500M' }}
                </div>
              </div>
              <div v-else class="file-ok">
                <i class="el-icon-document-checked"></i>
                <p>{{ form.fileName || '已选择代码包' }}</p>
                <p class="file-tip">已上传，点击可重新选择</p>
              </div>
            </el-upload>
            <p v-if="isEdit" class="field-tip">
              不重新选择 zip 则保留原代码包；替换后买家重新下载到的即为新包，请谨慎操作。
            </p>
          </el-form-item>
        </el-form>
        <div slot="footer" class="dialog-footer">
          <el-button type="primary" :loading="submitting" @click="submitForm">确 定</el-button>
          <el-button @click="dialogOpen = false">取 消</el-button>
        </div>
      </el-dialog>
    </div>
  </div>
</template>

<script>
import {
  getMyPluginDetail,
  listPluginVersions,
  addPluginVersion,
  editPluginVersion,
  deletePluginVersion,
  uploadEditorImage
} from '@/api/developer'
import { getDownLoadInfo, downloadPlugin } from '@/api/plugin'
import { getToken } from '@/utils/auth'
import { dateFormat, fenToYuan } from '@/utils/common'
import { mavonEditor } from 'mavon-editor'
import 'mavon-editor/dist/css/index.css'

const emptyForm = () => ({
  infoId: undefined,
  infoVersion: '',
  infoContent: '',
  infoUpdate: '',
  fileUrl: '',
  fileName: ''
})

export default {
  name: 'pluginVersion',
  components: { mavonEditor },
  data () {
    return {
      headers: { Authorization: 'Bearer ' + getToken() },
      loading: true,
      plugin: {},
      versions: [],
      dialogOpen: false,
      submitting: false,
      editVersionHadCode: false,
      editVersionCodeName: '',
      form: emptyForm(),
      rules: {
        infoVersion: [{ required: true, message: '版本号不能为空', trigger: 'blur' }],
        fileUrl: [{ validator: this.validateCodeFile, trigger: 'change' }]
      }
    }
  },
  computed: {
    pluginId () {
      return this.$route.query.pluginId
    },
    uploadAction () {
      return this.BaseUrl + '/codeStoreFrontAdmin/uploadPluginCode'
    },
    isEdit () {
      return !!this.form.infoId
    }
  },
  methods: {
    validateCodeFile (rule, value, callback) {
      // 新增版本必须上传代码包；编辑版本允许保留原代码包
      if (!this.isEdit && !value) {
        callback(new Error('请上传代码包'))
        return
      }
      callback()
    },
    formatYuan (value) {
      return fenToYuan(value).toFixed(2)
    },
    formatTime (value) {
      return value ? dateFormat(value, 'YYYY-MM-DD HH:mm:ss') : '-'
    },
    // 列表内只做纯文本摘要展示，去掉 Markdown 标记
    mdToPlain (text) {
      if (!text) {
        return ''
      }
      return String(text)
        .replace(/```[\s\S]*?```/g, ' [代码块] ')
        .replace(/`([^`]*)`/g, '$1')
        .replace(/!\[[^\]]*\]\([^)]*\)/g, '[图片]')
        .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
        .replace(/^\s{0,3}#{1,6}\s*/gm, '')
        .replace(/^\s{0,3}>\s?/gm, '')
        .replace(/^\s{0,3}[-*+]\s+/gm, '')
        .replace(/\*\*|__|~~|\*/g, '')
        .replace(/\s+/g, ' ')
        .trim()
    },
    loadPlugin () {
      getMyPluginDetail(this.pluginId).then(res => {
        const { code, data } = res
        if (code === 0) {
          this.plugin = data.info || {}
        }
      })
    },
    loadVersions () {
      this.loading = true
      listPluginVersions(this.pluginId).then(res => {
        const { code, data, msg } = res
        if (code === 0) {
          this.versions = Array.isArray(data) ? data : []
        } else {
          this.$message.error(msg || '获取版本列表失败')
        }
      }).finally(() => {
        this.loading = false
      })
    },
    openAdd () {
      this.form = emptyForm()
      this.editVersionHadCode = false
      this.editVersionCodeName = ''
      this.dialogOpen = true
      this.resetEditor()
    },
    openEdit (row) {
      this.form = {
        infoId: row.infoId,
        infoVersion: row.infoVersion || '',
        infoContent: row.infoContent || '',
        infoUpdate: row.infoUpdate || '',
        fileUrl: '',
        fileName: ''
      }
      this.editVersionHadCode = !!row.infoCode
      this.editVersionCodeName = row.codeFileName || ''
      this.dialogOpen = true
      this.resetEditor()
    },
    resetEditor () {
      this.$nextTick(() => {
        this.$refs.versionForm && this.$refs.versionForm.clearValidate()
      })
    },
    // 下载已上传的代码包（下载接口对插件归属人免购买校验、免每日次数限制）
    downloadCode (row) {
      const infoVersion = row && row.infoVersion
      if (!infoVersion) {
        this.$message.warning('缺少版本信息，无法下载')
        return
      }
      getDownLoadInfo({ pluginId: this.pluginId, version: infoVersion }).then(res => {
        const { code, data, msg } = res
        if (code === 0 && data) {
          // 下载走浏览器跳转，鉴权令牌随 URL 传递（与网站其它下载入口一致）
          downloadPlugin(this.BaseUrl + '/' + data + '&token=' + encodeURIComponent(getToken()))
        } else {
          this.$message.error(msg || '获取下载地址失败')
        }
      }).catch(() => {
        this.$message.error('获取下载地址失败，请稍后重试')
      })
    },
    beforeCodeUpload (file) {
      const isZip = file.name.toLowerCase().endsWith('.zip')
      const isLt500M = file.size / 1024 / 1024 < 500
      if (!isZip) {
        this.$message.error('代码包只能是 zip 压缩包')
      }
      if (!isLt500M) {
        this.$message.error('代码包大小不能超过 500M')
      }
      return isZip && isLt500M
    },
    handleCodeSuccess (res, file) {
      const { code, data, msg } = res
      if (code === 0 && data && data.fileInfo) {
        this.form.fileUrl = data.fileInfo.fileUrl
        this.form.fileName = data.fileInfo.fileName
        this.$refs.versionForm.validateField('fileUrl')
        this.$message.success('代码包上传成功')
      } else {
        this.$message.error(msg || '上传失败')
      }
    },
    handleCodeError () {
      this.$message.error('代码包上传失败，请检查网络或文件大小后重试')
    },
    handleContentImgAdd (pos, file) {
      this.uploadEditorImg(pos, file, 'mdContent')
    },
    handleUpdateImgAdd (pos, file) {
      this.uploadEditorImg(pos, file, 'mdUpdate')
    },
    // 编辑器内插图：上传成功后回填到光标位置
    uploadEditorImg (pos, file, refName) {
      const formData = new FormData()
      formData.append('file', file)
      uploadEditorImage(formData).then(res => {
        const { code, data, msg } = res
        if (code === 0 && data && data.fileUrl) {
          const editor = this.$refs[refName]
          editor && editor.$img2Url(pos, data.fileUrl)
        } else {
          this.$message.error(msg || '图片上传失败')
        }
      }).catch(() => {
        this.$message.error('图片上传失败，请稍后重试')
      })
    },
    submitForm () {
      this.$refs.versionForm.validate(valid => {
        if (!valid) return
        this.submitting = true
        const payload = {
          infoVersion: this.form.infoVersion,
          infoUpdate: this.form.infoUpdate,
          infoContent: this.form.infoContent
        }
        if (this.form.fileUrl) {
          payload.infoCodeData = [{ name: this.form.fileName, url: this.form.fileUrl }]
        }
        const request = this.isEdit
          ? editPluginVersion(Object.assign({ infoId: this.form.infoId }, payload))
          : addPluginVersion(Object.assign({ pluginId: this.pluginId }, payload))
        request.then(res => {
          const { code, msg } = res
          if (code === 0) {
            this.$message.success(msg || (this.isEdit ? '版本更新成功' : '版本添加成功'))
            this.dialogOpen = false
            this.loadVersions()
          } else {
            this.$message.error(msg || '保存失败')
          }
        }).catch(() => {
          this.$message.error('保存失败，请稍后重试')
        }).finally(() => {
          this.submitting = false
        })
      })
    },
    handleDelete (row) {
      this.$confirm(
        `确定删除版本 ${row.infoVersion} 吗？删除后买家将无法再下载该版本，此操作不可恢复。`,
        '删除确认',
        { type: 'warning', confirmButtonText: '确定删除', cancelButtonText: '取消' }
      ).then(() => {
        return deletePluginVersion(row.infoId)
      }).then(res => {
        const { code, msg } = res
        if (code === 0) {
          this.$message.success(msg || '删除成功')
          this.loadVersions()
        } else {
          this.$message.error(msg || '删除失败')
        }
      }).catch(() => {})
    },
    goBack () {
      this.$router.push({ path: 'developerMyPlugins' })
    }
  },
  created () {
    if (!this.pluginId) {
      this.$message.warning('缺少插件参数')
      this.goBack()
      return
    }
    this.loadPlugin()
    this.loadVersions()
  }
}
</script>

<style lang="less" scoped>
.plugin-version {
  .panel-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;

    h2 {
      margin: 0;
      font-size: 22px;
      color: #303133;
    }
  }

  .plugin-summary {
    background: #f5f7fa;
    border-radius: 6px;
    padding: 16px 20px;
    margin-bottom: 16px;

    .summary-title {
      margin: 0;
      font-size: 17px;
      font-weight: 600;
      color: #303133;
    }

    .summary-sub {
      margin: 6px 0 0 0;
      font-size: 13px;
      color: #909399;
    }
  }

  .alert-info-light {
    background-color: #d9edf7;
    border-color: #c6e4f3;
    color: #3a87ad;
    padding: 15px;
    margin-bottom: 17px;
    border: 1px solid transparent;
    border-radius: 3px;
    font-size: 13px;
    line-height: 22px;
  }

  .current-code {
    margin-bottom: 10px;
    font-size: 13px;
    color: #606266;

    .current-code-label {
      color: #909399;
    }

    .current-code-name {
      color: #303133;
      font-weight: 600;
      margin-right: 4px;
    }
  }

  .empty-box {
    padding: 50px 0;
    text-align: center;
    color: #909399;

    .empty-icon {
      font-size: 48px;
      color: #c0c4cc;
    }

    p {
      margin: 14px 0 18px 0;
    }
  }

  .field-tip {
    margin: 6px 0 0 0;
    font-size: 12px;
    color: #909399;
    line-height: 20px;
  }

  .upload-drag {
    i {
      font-size: 40px;
      color: #c0c4cc;
    }
  }

  .file-ok {
    padding: 16px 0;

    i {
      font-size: 36px;
      color: #67c23a;
    }

    p {
      margin: 8px 0 0 0;
      color: #303133;
    }

    .file-tip {
      font-size: 12px;
      color: #909399;
    }
  }
}
</style>
